import { useEffect, useState, useCallback } from "react";
import { TopBar } from "@/components/TopBar";
import { api, adminToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, LogOut } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";

function StatCard({ label, value }) {
  return (
    <div className="rounded-sm border border-neutral-200 p-6">
      <div className="text-3xl font-bold tracking-tight text-neutral-900">{value}</div>
      <div className="tw-eyebrow mt-2">{label}</div>
    </div>
  );
}

function AdminLogin({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.adminLogin({ email, password });
      adminToken.set(res.token);
      onAuthed();
    } catch {
      toast.error("Invalid email or password.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopBar step="Admin" />
      <main className="mx-auto flex max-w-md flex-col px-4 py-24 sm:px-6">
        <div className="flex items-center gap-3">
          <Lock className="h-5 w-5 text-[#1408F7]" strokeWidth={1.5} />
          <p className="tw-eyebrow">Restricted area</p>
        </div>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-neutral-900">Admin sign in</h1>
        <p className="mt-3 text-neutral-600">Enter your admin credentials to view respondent data.</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@threewordsfull.com"
              className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
              data-testid="admin-login-email-input"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 h-12 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7]"
              data-testid="admin-login-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="tw-btn-primary tw-focus w-full rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide disabled:opacity-40"
            data-testid="admin-login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true/false
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [pathway, setPathway] = useState("all");
  const [detail, setDetail] = useState(null);
  const [hsSyncing, setHsSyncing] = useState(false);
  const [journeySyncing, setJourneySyncing] = useState(false);

  // Check for an existing valid session on mount.
  useEffect(() => {
    if (!adminToken.get()) {
      setAuthed(false);
      return;
    }
    api
      .adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        adminToken.clear();
        setAuthed(false);
      });
  }, []);

  const load = useCallback(async () => {
    if (authed !== true) return;
    const params = {};
    if (q) params.q = q;
    if (pathway !== "all") params.pathway = pathway;
    try {
      const [s, list] = await Promise.all([api.adminStats(), api.adminList(params)]);
      setStats(s);
      setRows(list.respondents);
    } catch (e) {
      if (e?.response?.status === 401) {
        adminToken.clear();
        setAuthed(false);
      }
    }
  }, [q, pathway, authed]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = async (id) => {
    setDetail(await api.adminDetail(id));
  };

  const retryHubspot = async (id) => {
    setHsSyncing(true);
    try {
      await api.adminHubspotSync(id);
      setDetail(await api.adminDetail(id));
    } finally {
      setHsSyncing(false);
    }
  };

  const retryJourney = async (id) => {
    setJourneySyncing(true);
    try {
      await api.adminJourneySync(id);
      setDetail(await api.adminDetail(id));
    } finally {
      setJourneySyncing(false);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  const logout = async () => {
    try {
      await api.adminLogout();
    } catch {
      /* stateless — ignore */
    }
    adminToken.clear();
    setStats(null);
    setRows([]);
    setDetail(null);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <p className="tw-eyebrow text-neutral-400">Checking access…</p>
      </div>
    );
  }
  if (authed === false) {
    return <AdminLogin onAuthed={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <TopBar step="Admin" />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="tw-eyebrow">Respondent management</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-neutral-900">Diagnostics</h1>
          </div>
          <button
            onClick={logout}
            className="tw-focus inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-700 hover:border-neutral-900"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="Completed" value={stats.completed} />
            <StatCard label="Leads" value={stats.leads} />
            <StatCard label="Lead rate" value={`${stats.lead_rate}%`} />
            <StatCard label="Marketing / Leadership" value={`${stats.marketing} / ${stats.leadership}`} />
          </div>
        )}

        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or organisation…"
            className="h-11 rounded-sm border-neutral-300 focus-visible:ring-[#1408F7] sm:max-w-xs"
            data-testid="admin-search-input"
          />
          <Select value={pathway} onValueChange={setPathway}>
            <SelectTrigger
              className="h-11 w-full rounded-sm border-neutral-300 sm:w-48"
              data-testid="admin-filter-pathway-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pathways</SelectItem>
              <SelectItem value="marketing">Marketing</SelectItem>
              <SelectItem value="leadership">Leadership</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 overflow-x-auto rounded-sm border border-neutral-200">
          <table className="w-full text-left text-sm" data-testid="admin-respondents-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="tw-eyebrow text-neutral-500">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Organisation</th>
                <th className="px-4 py-3 font-semibold">Pathway</th>
                <th className="px-4 py-3 font-semibold">Archetype</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-neutral-400">
                    No respondents yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => openDetail(r.id)}
                  className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                  data-testid="admin-respondent-row-item"
                >
                  <td className="px-4 py-3 font-medium text-neutral-900">{r.about?.name || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.about?.organisation || "—"}</td>
                  <td className="px-4 py-3 capitalize text-neutral-600">{r.pathway}</td>
                  <td className="px-4 py-3 text-neutral-600">
                    {r.summary?.primary_archetype || r.results?.primary_archetype?.name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={`rounded-sm ${
                        r.lead ? "border-[#1408F7] text-[#1408F7]" : "border-neutral-300 text-neutral-500"
                      }`}
                    >
                      {r.lead ? "Lead" : r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-neutral-500">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-respondent-detail-drawer">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-left text-2xl tracking-tight">
                  {detail.about?.name || "Respondent"}
                </SheetTitle>
                <SheetDescription className="text-left">
                  Full respondent record, scores and contact status.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-6 text-sm">
                <Field label="Pathway" value={detail.pathway} />
                <Field label="Job title" value={detail.about?.job_title} />
                <Field label="Organisation" value={detail.about?.organisation} />
                <Field label="Organisation size" value={detail.about?.organisation_size} />
                <Field label="Marketing team size" value={detail.about?.marketing_team_size} />
                <Field label="Sector" value={detail.about?.sector} />
                <Field label="Status" value={detail.status} />

                {detail.results && (
                  <div>
                    <p className="tw-eyebrow mb-2">Archetype</p>
                    <p className="text-neutral-900">
                      {detail.results.primary_archetype?.name}
                      {detail.results.secondary_archetype?.name
                        ? ` · ${detail.results.secondary_archetype.name} (secondary)`
                        : ""}
                    </p>
                    <p className="mt-1 text-neutral-500">
                      Confidence gap: {detail.results.archetype_confidence_gap}
                    </p>
                  </div>
                )}

                <div className="rounded-sm border border-neutral-200 p-4">
                  <p className="tw-eyebrow mb-2">Results saved</p>
                  <p className="text-neutral-900">{detail.results_saved ? "Yes" : "No"}</p>
                  {detail.lead && (
                    <>
                      <p className="mt-2 text-neutral-900">{detail.lead.email}</p>
                      <p className="text-neutral-600">
                        Conversation requested: {detail.conversation_requested ? "Yes" : "No"}
                      </p>
                      <p className="text-neutral-600">Email status: {detail.email_status || "—"}</p>
                      <p className="text-neutral-500">Sent: {detail.results_sent_at ? new Date(detail.results_sent_at).toLocaleString() : "—"}</p>
                    </>
                  )}
                </div>

                {detail.lead && (
                  <div className="rounded-sm border border-neutral-200 p-4" data-testid="admin-hubspot-block">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="tw-eyebrow">HubSpot CRM</p>
                      <button
                        type="button"
                        onClick={() => retryHubspot(detail.id)}
                        disabled={hsSyncing}
                        className="rounded-sm border border-[#1408F7] px-3 py-1 text-xs font-semibold text-[#1408F7] disabled:opacity-50"
                        data-testid="admin-hubspot-retry-button"
                      >
                        {hsSyncing ? "Syncing…" : detail.hubspot_synced ? "Re-sync" : "Sync now"}
                      </button>
                    </div>
                    <p className="text-neutral-900" data-testid="admin-hubspot-synced">
                      Synced: {detail.hubspot_synced ? "Yes" : "No"}
                    </p>
                    {detail.hubspot_contact_id && (
                      <p className="text-neutral-600">Contact ID: {detail.hubspot_contact_id}</p>
                    )}
                    <p className="text-neutral-600">Status: {detail.hubspot_sync_status || "—"}</p>
                    {detail.hubspot_sync_error && (
                      <p className="mt-1 break-words text-red-600">Error: {detail.hubspot_sync_error}</p>
                    )}
                    <p className="text-neutral-500">
                      Last sync: {detail.hubspot_last_sync_at ? new Date(detail.hubspot_last_sync_at).toLocaleString() : "—"}
                    </p>
                  </div>
                )}

                {detail.results && (
                  <div className="rounded-sm border border-neutral-200 p-4" data-testid="admin-journey-block">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="tw-eyebrow">D2 Journey</p>
                      <button
                        type="button"
                        onClick={() => retryJourney(detail.id)}
                        disabled={journeySyncing}
                        className="rounded-sm border border-[#1408F7] px-3 py-1 text-xs font-semibold text-[#1408F7] disabled:opacity-50"
                        data-testid="admin-journey-retry-button"
                      >
                        {journeySyncing ? "Syncing…" : "Retry Journey sync"}
                      </button>
                    </div>
                    <p className="text-neutral-900" data-testid="admin-journey-status">
                      Status: {detail.journey_sync_status || "not synced"}
                    </p>
                    {detail.journey_id && (
                      <p className="text-neutral-600">Journey ID: {detail.journey_id}</p>
                    )}
                    {detail.participant_id && (
                      <p className="text-neutral-600">Participant ID: {detail.participant_id}</p>
                    )}
                    {detail.journey_sync_error && (
                      <p className="mt-1 break-words text-red-600">Error: {detail.journey_sync_error}</p>
                    )}
                    <p className="text-neutral-500">
                      Last sync: {detail.journey_last_sync_at ? new Date(detail.journey_last_sync_at).toLocaleString() : "—"}
                    </p>
                  </div>
                )}

                {detail.counterpart && (
                  <div className="rounded-sm border border-neutral-200 p-4">
                    <p className="tw-eyebrow mb-2">Counterpart invite</p>
                    <p className="text-neutral-900">
                      {detail.counterpart.name} · {detail.counterpart.email}
                    </p>
                    <p className="text-neutral-500">{detail.counterpart.role}</p>
                  </div>
                )}

                <div>
                  <p className="tw-eyebrow mb-2">Answers</p>
                  <p className="text-neutral-500">
                    {detail.answers?.length || 0} responses recorded.
                  </p>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between border-b border-neutral-100 pb-2">
      <span className="tw-eyebrow">{label}</span>
      <span className="capitalize text-neutral-900">{value || "—"}</span>
    </div>
  );
}
